/**
 * CoPilot Guide JSON Schema (Zod)
 *
 * Runtime validation contract for `CoPilotGuideConfig` JSONs in
 * `apps/brain-service/src/modules/brain/learning/*`. Catches the
 * defects flagged in the 2026-04-26 audit at config-load time
 * instead of at runtime when a user trips over them:
 *
 *   • embeddedFeature chats lacking `onCancel` (would stall the flow)
 *   • Empty widget configs (`config: {}`)
 *   • Variables referenced in conditions but never set
 *   • Missing terminal/escape tasks
 *   • Naming-convention drift (taskN_name)
 *
 * Used by:
 *   • brain-service config loader (rejects bad configs at boot)
 *   • a CLI script that lints all 5 configs in CI
 *   • unit tests that snapshot the schema as the canonical contract
 */

import { z } from 'zod';

// ── Core building blocks ─────────────────────────────────────────────

const senderSchema = z.enum(['assistant', 'user', 'system']);

const messageSchema = z.object({
  id: z.string().min(1),
  text: z.string().min(1),
  sender: senderSchema,
  delay: z.number().int().nonnegative().optional(),
  isFirstInGroup: z.boolean().optional(),
});

const styleSchema = z.enum(['primary', 'secondary', 'tertiary', 'danger']);

const buttonActionSchema = z.object({
  id: z.string().min(1),
  type: z.literal('button'),
  text: z.string().min(1),
  style: styleSchema,
  transition: z.string().optional(),
  condition: z.string().optional(),
  effects: z.array(z.string()).optional(),
});

const textInputActionSchema = z.object({
  id: z.string().min(1),
  type: z.literal('text_input'),
  placeholder: z.string().optional(),
  text: z.string().optional(),
  style: styleSchema.optional(),
  dataPointId: z.string().min(1),
  transition: z.string().optional(),
  condition: z.string().optional(),
  effects: z.array(z.string()).optional(),
});

const actionSchema = z.discriminatedUnion('type', [buttonActionSchema, textInputActionSchema]);

const chatTypeSchema = z.enum(['messageSequence', 'embeddedFeature', 'confirmation']);

const chatMetadataSchema = z
  .object({
    task: z.string().optional(),
    effects: z.array(z.string()).optional(),
    next_is_feature: z.boolean().optional(),
    feature_state: z.string().optional(),
    feature: z.string().optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    completionMessage: z.string().optional(),
    onComplete: z.string().optional(),
    onCancel: z.string().optional(),
    cancelMessage: z.string().optional(),
    auto_transition: z.string().optional(),
    delay: z.number().int().nonnegative().optional(),
    skip_if: z.string().optional(),
    rebuildTarget: z.string().optional(),
    /** Custom host-side effects (call_instantiation, save_draft, etc.) */
    instantiation: z.record(z.string(), z.unknown()).optional(),
  })
  .loose();

const chatSchema = z.object({
  id: z.string().min(1),
  type: chatTypeSchema,
  messages: z.array(messageSchema).optional(),
  actions: z.array(actionSchema).optional(),
  metadata: chatMetadataSchema.optional(),
});

const taskTypeSchema = z.string().min(1);

const taskSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  type: taskTypeSchema,
  avgTime: z.string().min(1),
  description: z.string(),
  iconDescription: z.string().optional(),
  iconType: z.string().optional(),
  chats: z.array(chatSchema).min(1),
});

export const coPilotGuideConfigSchema = z.object({
  id: z.string().min(1),
  kind: z.string().min(1),
  version: z.string().min(1),
  active: z.boolean(),
  scope: z.object({
    tenant: z.string().min(1),
    market: z.string().min(1),
    language: z.string().min(1),
  }),
  content: z.object({
    tasks: z.array(taskSchema).min(1),
  }),
  audit: z
    .object({
      createdAt: z.string().optional(),
      updatedAt: z.string().optional(),
      changelog: z.array(z.unknown()).optional(),
    })
    .optional(),
});

// ── Cross-cutting structural rules ──────────────────────────────────
//
// Zod alone validates shape. The audit identified flow-level rules
// that need second-pass validation: chat ID references are real,
// every embeddedFeature has onCancel, terminal task exists, etc.

export interface GuideConfigViolation {
  level: 'error' | 'warning';
  taskId?: string;
  chatId?: string;
  code: string;
  message: string;
}

export type GuideConfigForValidation = z.infer<typeof coPilotGuideConfigSchema>;

/**
 * Run structural rules on top of the Zod-validated shape. Returns the
 * (possibly empty) list of violations. Errors should fail config
 * loading; warnings should be logged.
 */
export function validateGuideStructure(config: GuideConfigForValidation): GuideConfigViolation[] {
  const out: GuideConfigViolation[] = [];

  // Build a flat map of chat IDs.
  const chatIds = new Set<string>();
  for (const t of config.content.tasks) {
    for (const c of t.chats) {
      if (chatIds.has(c.id)) {
        out.push({
          level: 'error',
          taskId: t.id,
          chatId: c.id,
          code: 'duplicate_chat_id',
          message: `Chat id "${c.id}" is duplicated`,
        });
      }
      chatIds.add(c.id);
    }
  }

  // Chat-ID references are real targets.
  const checkRef = (ref: string | undefined, taskId: string, chatId: string, field: string) => {
    if (!ref) return;
    if (ref === 'escape' || ref === 'terminal') return; // engine sentinels
    if (!chatIds.has(ref)) {
      out.push({
        level: 'error',
        taskId,
        chatId,
        code: 'unknown_chat_reference',
        message: `${field} "${ref}" does not match any chat id in the config`,
      });
    }
  };

  let hasTerminalTask = false;
  let hasEscapeTask = false;
  for (const t of config.content.tasks) {
    if (t.type === 'Terminal') hasTerminalTask = true;
    if (t.type === 'Escape') hasEscapeTask = true;

    for (const c of t.chats) {
      // Embedded features must declare both onComplete and onCancel —
      // missing onCancel was the #1 trap state in the audit.
      if (c.type === 'embeddedFeature') {
        if (!c.metadata?.onComplete) {
          out.push({
            level: 'error',
            taskId: t.id,
            chatId: c.id,
            code: 'missing_on_complete',
            message: 'embeddedFeature chats must declare metadata.onComplete',
          });
        }
        if (!c.metadata?.onCancel) {
          out.push({
            level: 'error',
            taskId: t.id,
            chatId: c.id,
            code: 'missing_on_cancel',
            message:
              'embeddedFeature chats must declare metadata.onCancel — without it the engine returns null on cancel and the flow stalls',
          });
        }
        if (
          c.metadata?.feature &&
          (!c.metadata.config || Object.keys(c.metadata.config).length === 0)
        ) {
          out.push({
            level: 'warning',
            taskId: t.id,
            chatId: c.id,
            code: 'empty_widget_config',
            message:
              'Widget config is empty — widget will fall back to hardcoded defaults; declarative options are easier to audit',
          });
        }
      }

      checkRef(c.metadata?.onComplete, t.id, c.id, 'metadata.onComplete');
      checkRef(c.metadata?.onCancel, t.id, c.id, 'metadata.onCancel');
      checkRef(c.metadata?.auto_transition, t.id, c.id, 'metadata.auto_transition');
      checkRef(c.metadata?.rebuildTarget, t.id, c.id, 'metadata.rebuildTarget');
      for (const action of c.actions ?? []) {
        checkRef(action.transition, t.id, c.id, `actions[${action.id}].transition`);
      }
    }
  }

  if (!hasTerminalTask) {
    out.push({
      level: 'error',
      code: 'missing_terminal_task',
      message: 'Config must include at least one task with type="Terminal" so the flow can end',
    });
  }
  if (!hasEscapeTask) {
    out.push({
      level: 'warning',
      code: 'missing_escape_task',
      message:
        'Config has no task with type="Escape" — users have no graceful early-exit other than closing the tab',
    });
  }

  return out;
}

/**
 * Convenience: parse + structural validate. Throws on any error-level
 * violation (Zod or structural). Returns the typed config + warnings.
 */
export function parseGuideConfig(raw: unknown): {
  config: GuideConfigForValidation;
  warnings: GuideConfigViolation[];
} {
  const config = coPilotGuideConfigSchema.parse(raw);
  const violations = validateGuideStructure(config);
  const errors = violations.filter((v) => v.level === 'error');
  if (errors.length > 0) {
    const msg = errors
      .map((e) => `[${e.code}] ${e.taskId ?? ''}/${e.chatId ?? ''}: ${e.message}`)
      .join('\n');
    throw new Error(`CoPilot guide config has ${errors.length} structural error(s):\n${msg}`);
  }
  return { config, warnings: violations.filter((v) => v.level === 'warning') };
}
